import React from "react"
import { FiCheck } from "react-icons/fi"
import { BsThreeDots } from "react-icons/bs"
import { RiDeleteBinLine } from "react-icons/ri"
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  IconButton,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverTrigger,
  Tag,
  Text,
  useDisclosure,
} from "@chakra-ui/react"

import { tagColors } from "../lib/static/tagColors"
import { TagDeclaration } from "../lib/types/customTag"

interface Props {
  tag: TagDeclaration
  active: boolean
  handleAssign: (id: string) => void
  handleDelete: (id: string) => void
  handleUpdateColor: (id: string, color: string) => void
  handleUpdateName: (id: string, name: string) => void
}

export const TagItem: React.FC<Props> = ({
  tag,
  active,
  handleAssign,
  handleDelete,
  handleUpdateColor,
  handleUpdateName,
}) => {
  const { isOpen, onClose, onOpen } = useDisclosure()
  return (
    <Flex
      key={tag.uuid}
      align="center"
      justify="space-between"
      bg={active ? "#EFEFEF" : undefined}
      _hover={{ bg: "#EFEFEF" }}
      cursor="pointer"
      className="tag"
    >
      <Flex px={3} py={1} w="100%" onClick={() => handleAssign(tag.uuid || "")}>
        <Tag mr={2} bg={tag.color} size="sm" userSelect="none">
          {tag.name}
        </Tag>
      </Flex>
      <Popover isLazy>
        <PopoverTrigger>
          <Box h="20px" w="35px" pr={3}>
            <IconButton
              aria-label="Tag options"
              icon={<BsThreeDots style={{ margin: "0 auto" }} />}
              variant="ghost"
              display="none"
              h="100%"
              w="100%"
              minW={0}
              borderRadius={2}
              _hover={{ bg: "#E1E1E1" }}
              _active={{ bg: "none" }}
              sx={{ ".tag:hover &": { display: "block" } }}
            />
          </Box>
        </PopoverTrigger>
        <PopoverContent w="200px">
          <PopoverBody px={0}>
            <Box px={2}>
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  handleUpdateName(tag.uuid, e.target.elements.name.value)
                }}
              >
                <Input name="name" size="sm" defaultValue={tag.name} />
              </form>
            </Box>
            <Popover isLazy isOpen={isOpen} onClose={onClose} onOpen={onOpen}>
              <PopoverTrigger>
                <Button
                  pr="125px"
                  py={1}
                  my={2}
                  size="sm"
                  variant="ghost"
                  w="100%"
                  borderRadius={0}
                  leftIcon={<RiDeleteBinLine />}
                  _hover={{ bg: "#EFEFEF" }}
                >
                  Delete
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <PopoverArrow />
                <PopoverBody>Are you sure you want to delete this tag?</PopoverBody>
                <PopoverFooter display="flex" justifyContent="flex-end">
                  <ButtonGroup size="sm">
                    <Button variant="outline" onClick={onClose}>
                      Cancel
                    </Button>
                    <Button colorScheme="red" onClick={() => handleDelete(tag.uuid || "")}>
                      Delete
                    </Button>
                  </ButtonGroup>
                </PopoverFooter>
              </PopoverContent>
            </Popover>
            <Divider mb={2} />
            <Text px={2} mb={2} fontSize="0.8rem" color="gray.400" fontWeight="bold">
              COLORS
            </Text>
            {tagColors.map((tagColor, index) => (
              <Flex
                key={index}
                align="center"
                justify="space-between"
                py={1}
                px={2}
                _hover={{ bg: "#EFEFEF" }}
                onClick={() => handleUpdateColor(tag.uuid || "", tagColor.color)}
              >
                <Flex align="center">
                  <Box
                    bg={tagColor.color + "AA"}
                    mr={2}
                    h="18px"
                    w="18px"
                    borderRadius={2}
                    border="1px solid"
                    borderColor={tagColor.color}
                  />
                  <Text fontSize="0.9rem">{tagColor.name}</Text>
                </Flex>
                {tag.color === tagColor.color && <Box as={FiCheck} />}
              </Flex>
            ))}
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Flex>
  )
}
